import { Button, Icon, Shadow, Text, HStack, VStack } from '@genial/components';
import { BoxRadius, Color, Spacing, Typography } from '@genial/design-tokens';
import { useBreakpointValue } from '@genial/utils';
import { RouteProp, useNavigation, useRoute } from '@react-navigation/native';
import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { CustomerDetailTypes } from '../..';
import { useRecoilQuery } from '../../../../hooks/useRecoilQuery';
import {
  SignedInNavigationScreenNavigationProp,
  CustomerDetailStackParamList,
} from '../../../../routes/types';
import { groupedCustomerDetailQuery } from '../../../../store/customerDetail/selector';
import { getWithdrawalEligibility } from '../../../../store/customerDetail/withdrawal/constants';
import { customerWithdrawalsQuery } from '../../../../store/customerDetail/withdrawal/selector';
import { CustomerWithdrawalItemNormalized } from '../../../../store/customerDetail/withdrawal/types';

import { WithdrawalEligibilityTooltip } from './components/NewWithdrawal/components/WithdrawalEligibilityTooltip';
import { WithdrawalList } from './components/WithdrawalList';

export const Withdrawal: React.FC<CustomerDetailTypes.Props> = () => {
  const route = useRoute<RouteProp<CustomerDetailStackParamList, 'CustomerDetail'>>();
  const [page, setPage] = useState(1);
  const [allData, setAllData] = useState<CustomerWithdrawalItemNormalized[]>([]);
  const { data, refresher, error, loading } = useRecoilQuery(
    customerWithdrawalsQuery({ page, accountId: route.params.accountId })
  );
  const {
    data: customerData,
    loading: customerLoading,
    error: customerError,
  } = useRecoilQuery(groupedCustomerDetailQuery({ accountNumber: route.params.accountNumber }));

  const navigation = useNavigation<SignedInNavigationScreenNavigationProp>();
  const isMobile = useBreakpointValue({
    base: true,
    sm: false,
  });

  const withdrawalEligibility = useMemo(
    () => getWithdrawalEligibility(customerData, customerLoading, !!customerError),
    [customerData, customerLoading, customerError]
  );

  const handleNewWithdrawal = useCallback(() => {
    navigation.navigate('NewWithdrawal', {
      accountId: route.params.accountId,
      customerId: route.params.customerId,
      accountNumber: route.params.accountNumber,
    });
  }, [navigation, route.params.accountId, route.params.accountNumber, route.params.customerId]);

  const handleRefresh = useCallback(() => {
    setPage(1);
    setAllData([]);
    refresher();
  }, [refresher]);

  const handleEndReached = useCallback(() => {
    if (!data?.meta.pagination.hasNext || loading || error) {
      return;
    }

    setPage((prev) => prev + 1);
  }, [data?.meta.pagination.hasNext, error, loading, setPage]);

  useEffect(() => {
    if (!data?.data) {
      return;
    }

    if (page === 1) {
      setAllData(data.data);
    } else {
      setAllData((prev) => [...prev, ...data.data]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data?.data]);

  const withdrawalActions = (
    <HStack alignItems="center" justifyContent={{ base: 'space-between', sm: '' }} width="100%">
      <Text flex={1} variant="semibold" fontSize={Typography['fontsize.md']}>
        Minhas Solicitações
      </Text>
      <WithdrawalEligibilityTooltip tooltipMessage={withdrawalEligibility.tooltipMessage}>
        <Button
          onPress={handleNewWithdrawal}
          variant="textLink"
          isDisabled={withdrawalEligibility.isBlocked}
          rightIcon={
            isMobile ? undefined : (
              <Icon name="Capture" size="xxs" color={Color['brand.tertiary.principal']} />
            )
          }>
          Novo Saque
        </Button>
      </WithdrawalEligibilityTooltip>
      {!isMobile && (
        <Button
          variant="textLink"
          rightIcon={<Icon name="Refresh" size="xxs" color={Color['brand.tertiary.principal']} />}
          onPress={handleRefresh}>
          Atualizar lista
        </Button>
      )}
    </HStack>
  );

  const withdrawalContent = (
    <VStack space={Spacing['stack.xxs']}>
      {isMobile ? (
        <Shadow level={1} padding={Spacing['inset.xs']} borderRadius={BoxRadius.sm}>
          {withdrawalActions}
        </Shadow>
      ) : (
        withdrawalActions
      )}
      <WithdrawalList
        isMobile={isMobile}
        error={error}
        data={allData}
        isLoading={loading}
        onEndReached={handleEndReached}
        refresher={refresher}
      />
    </VStack>
  );

  if (!isMobile) {
    return (
      <Shadow level={1} borderRadius={BoxRadius.sm} padding={Spacing['inset.sm']}>
        {withdrawalContent}
      </Shadow>
    );
  }

  return withdrawalContent;
};
